import { useCallback, useEffect, useMemo, useState } from "react";

import {
	useGetCsvEnrichmentContentQuery,
	useSaveCsvMappingMutation,
	useSuggestCsvMappingMutation,
} from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { List } from "../../schema/list";
import { useCompanyUploadPreview } from "../../../CsvEnrichment/components/titleGate/useCompanyUploadPreview";
import { useTitleScreeningPreview } from "../../../CsvEnrichment/components/titleGate/useTitleScreeningPreview";
import { FeaturesCodes } from "../../../Users/enum/codes";
import {
	applySuggestion,
	emptyMapping,
	FieldMappingEntry,
	findPreviousFile,
	mappingOptionsFor,
	meetsMinimumFields,
	minimumFieldsFor,
	newColumnsOf,
	parseHeaders,
	parseSampleRows,
	pickMappingTarget,
	toParsedXlsxData,
} from "./fieldMapping";

export type { FieldMappingEntry, MappingOption } from "./fieldMapping";

/**
 * Owns the field mapping of the file a list is about to import (PREC-3091).
 *
 * Each delivered file is mapped from scratch — a re-scrape can rename, drop or add columns, so
 * inheriting the previous file's mapping would quietly mis-map it. To keep that cheap the mapping
 * is pre-filled by the suggestion endpoint, and columns the previous file did not have are flagged
 * so the user knows where to look.
 */
export const useFieldMapping = (list: List) => {
	const csvEnrichments = useMemo(() => list.csvEnrichments || [], [list.csvEnrichments]);

	const target = useMemo(() => pickMappingTarget(csvEnrichments), [csvEnrichments]);
	const previousFile = useMemo(
		() => findPreviousFile(csvEnrichments, target),
		[csvEnrichments, target]
	);

	const { data: targetContent } = useGetCsvEnrichmentContentQuery(target?.id ?? 0, {
		skip: !target?.id,
	});
	const { data: previousContent } = useGetCsvEnrichmentContentQuery(previousFile?.id ?? 0, {
		skip: !previousFile?.id,
	});

	const [saveCsvMapping, { isLoading: isSaving }] = useSaveCsvMappingMutation();
	const [suggestCsvMapping] = useSuggestCsvMappingMutation();

	const [foundFields, setFoundFields] = useState<FieldMappingEntry[]>([]);
	const [screenTitles, setScreenTitles] = useState<boolean>(false);
	const [isSuggesting, setIsSuggesting] = useState<boolean>(false);
	const [suggestedForFileId, setSuggestedForFileId] = useState<number | null>(null);

	const defaultOptions = useMemo(() => mappingOptionsFor(list.entityType), [list.entityType]);
	const minimumFields = useMemo(() => minimumFieldsFor(list.entityType), [list.entityType]);

	const headers = useMemo(() => parseHeaders(targetContent), [targetContent]);

	const isTitleScreeningActive = useIsFeatureActive(FeaturesCodes.JOB_TITLE_SCREENING);
	const parsedData = useMemo(() => toParsedXlsxData(targetContent), [targetContent]);

	// The gate reacts to the *current* mapping, so it appears as soon as a title column is mapped.
	const { gateVisible, statsVisible, preview, rowStats } = useTitleScreeningPreview({
		entityType: list.entityType,
		foundFields,
		parsedData,
		isFeatureActive: isTitleScreeningActive,
	});

	// PREC-3870: a company source has no job titles to screen, but it still has rows to count.
	const { preview: companyPreview } = useCompanyUploadPreview({
		entityType: list.entityType,
		foundFields,
		parsedData,
	});

	const newColumns = useMemo(
		() => newColumnsOf(headers, parseHeaders(previousContent)),
		[headers, previousContent]
	);

	/** The mapping is per file, so a saved one is only reused for the file it belongs to. */
	const savedConfiguration = useMemo(() => {
		const configuration = target?.configuration;

		return Array.isArray(configuration) ? (configuration as FieldMappingEntry[]) : [];
	}, [target?.configuration]);

	useEffect(() => {
		setScreenTitles(!!target?.screenTitles);
	}, [target?.id, target?.screenTitles]);

	// Reset to the target file's rows whenever the file changes.
	useEffect(() => {
		if (!headers.length) {
			return;
		}

		setFoundFields(savedConfiguration.length ? savedConfiguration : emptyMapping(headers));
	}, [headers, savedConfiguration]);

	/**
	 * The lock is per file: an imported file can no longer be re-mapped, but a newly delivered one
	 * stays mappable next to it — that is what makes a re-scrape reviewable.
	 */
	const alreadyImported = !!target?.isImported;

	const persistMapping = useCallback(
		async (configuration: FieldMappingEntry[], screenTitlesToSave?: boolean) => {
			if (!target?.id) {
				return;
			}

			try {
				await saveCsvMapping({
					id: target.id,
					listId: list.id,
					configuration,
					screenTitles: screenTitlesToSave,
				}).unwrap();
			} catch (err) {
				console.error(err);
			}
		},
		[target?.id, list.id, saveCsvMapping]
	);

	// Pre-fill an unmapped file once, so "from scratch" still means a couple of clicks.
	useEffect(() => {
		const fileId = target?.id;

		if (
			!fileId ||
			!headers.length ||
			savedConfiguration.length ||
			suggestedForFileId === fileId ||
			!list.entityType
		) {
			return;
		}

		setSuggestedForFileId(fileId);
		setIsSuggesting(true);

		suggestCsvMapping({
			entityType: list.entityType as CsvEnrichmentType,
			headers,
			sampleRows: parseSampleRows(targetContent),
		})
			.unwrap()
			.then((result) => {
				if (!result.mapping?.length) {
					return;
				}

				const suggested = applySuggestion(headers, result.mapping, defaultOptions);

				setFoundFields(suggested);

				// PREC-3922: a suggestion that only lives in component state reads as "already
				// mapped" and is lost the moment the user navigates away — `configuration` stays
				// NULL, the run finds no mapping, and the customer gets no leads. Persist it so
				// what is on screen is what the file actually carries.
				//
				// Not saved when the suggestion falls short of the minimum fields (it could not
				// import anyway, so leave the file parked for the user), nor for an imported file
				// (the API rejects that with CSV_ALREADY_IMPORTED). `screenTitles` is left alone:
				// the user has not touched the toggle, so the file keeps its stored value.
				if (!alreadyImported && meetsMinimumFields(suggested, minimumFields)) {
					void persistMapping(suggested);
				}
			})
			.catch(() => {
				// Suggestion failed — the empty mapping stands and the user fills it in.
			})
			.finally(() => setIsSuggesting(false));
	}, [
		target?.id,
		headers,
		savedConfiguration.length,
		suggestedForFileId,
		list.entityType,
		targetContent,
		defaultOptions,
		suggestCsvMapping,
		alreadyImported,
		minimumFields,
		persistMapping,
	]);

	const allowUpload = useMemo(
		() => meetsMinimumFields(foundFields, minimumFields),
		[foundFields, minimumFields]
	);

	/** Options still selectable — a system field can only be assigned to one column. */
	const options = useMemo(() => {
		return defaultOptions.filter((option) => {
			if (option.id === "customField" || option.id === "") {
				return true;
			}

			return !foundFields.map((field) => field.assignedField).includes(option.id);
		});
	}, [defaultOptions, foundFields]);

	const saveMapping = useCallback(
		() =>
			// Only meaningful while the gate is on screen; the API re-checks the feature anyway.
			persistMapping(foundFields, gateVisible ? screenTitles : undefined),
		[persistMapping, foundFields, gateVisible, screenTitles]
	);

	return {
		allowUpload,
		alreadyImported,
		gateVisible,
		statsVisible,
		preview,
		companyPreview,
		rowStats,
		screenTitles,
		setScreenTitles,
		defaultOptions,
		foundFields,
		isSaving,
		isSuggesting,
		newColumns,
		options,
		saveMapping,
		setFoundFields,
		targetFileName: target?.name,
	};
};
